import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { reports as reportsApi } from "../lib/api";

type Bureau = "equifax" | "experian" | "transunion";
const BUREAUS: Bureau[] = ["equifax", "experian", "transunion"];

interface ParsedTradeline {
  creditor_name: string;
  account_number_last4?: string;
  bureaus: Bureau[];
  balances?: Partial<Record<Bureau, number>>;
  statuses?: Partial<Record<Bureau, string>>;
  late_payment_months?: string[];
  date_opened?: string;
  is_negative?: boolean;
  category?: string;
  account_type?: Partial<Record<Bureau, string>>;
  account_ownership?: Partial<Record<Bureau, string>>;
}

interface ParsedInquiry {
  bureau: Bureau;
  creditor_name: string;
  inquiry_date?: string;
  // Set by the analyzer's inquiry-linker pass. When `false`, the row is
  // linked to an open tradeline from the same creditor and was excluded
  // from analysis findings; we still render it here with a grey badge.
  is_disputable?: boolean;
  linked_account_creditor?: string;
}

interface ParsedPublicRecord {
  bureau: Bureau;
  kind: string;
  filing_date?: string;
  amount?: number;
}

interface FieldValueAcrossBureaus {
  equifax?: string[];
  experian?: string[];
  transunion?: string[];
}

interface ParsedPersonalInfo {
  names: FieldValueAcrossBureaus;
  addresses: FieldValueAcrossBureaus;
  employers: FieldValueAcrossBureaus;
  phones: FieldValueAcrossBureaus;
  dob: FieldValueAcrossBureaus;
}

interface ParsedReport {
  scores?: { equifax?: number; experian?: number; transunion?: number };
  personal_info?: ParsedPersonalInfo;
  tradelines?: ParsedTradeline[];
  inquiries?: ParsedInquiry[];
  public_records?: ParsedPublicRecord[];
  account_summary?: Record<string, number | undefined>;
  warnings?: string[];
}

type TabKey = "personal" | "tradelines" | "inquiries" | "public" | "other";

const TAB_LABELS: Record<TabKey, string> = {
  personal: "Personal",
  tradelines: "Tradelines",
  inquiries: "Inquiries",
  public: "Public Records",
  other: "Other",
};

function hasPersonal(p: ParsedReport | null): boolean {
  if (!p?.personal_info) return false;
  const pi = p.personal_info;
  return Boolean(
    (pi.names && Object.keys(pi.names).length) ||
    (pi.addresses && Object.keys(pi.addresses).length) ||
    (pi.employers && Object.keys(pi.employers).length) ||
    (pi.phones && Object.keys(pi.phones).length) ||
    (pi.dob && Object.keys(pi.dob).length)
  );
}

function hasTradelines(p: ParsedReport | null): boolean {
  return !!(p?.tradelines && p.tradelines.length > 0);
}
function hasInquiries(p: ParsedReport | null): boolean {
  return !!(p?.inquiries && p.inquiries.length > 0);
}
function hasPublic(p: ParsedReport | null): boolean {
  return !!(p?.public_records && p.public_records.length > 0);
}
// "Other" carries collections/charge-offs surfaced from tradelines + warnings + scores.
function hasOther(p: ParsedReport | null): boolean {
  if (!p) return false;
  const collections = (p.tradelines || []).filter((t) => isCollectionOrChargeOff(t));
  return Boolean(
    collections.length > 0 ||
    (p.warnings && p.warnings.length > 0) ||
    (p.scores && Object.keys(p.scores).length > 0) ||
    (p.account_summary && Object.keys(p.account_summary).length > 0)
  );
}

function isCollectionOrChargeOff(t: ParsedTradeline): boolean {
  if (t.is_negative) return true;
  if (t.category && t.category.startsWith("negative")) return true;
  const statuses = Object.values(t.statuses || {}).map((s) => String(s).toLowerCase());
  return statuses.some((s) => s.includes("collection") || s.includes("charge"));
}

function fieldRow(label: string, field: FieldValueAcrossBureaus | undefined) {
  if (!field) return null;
  const filled = BUREAUS.filter((b) => (field[b]?.length ?? 0) > 0);
  if (filled.length === 0) return null;
  return (
    <tr key={label}>
      <td style={{ fontWeight: 600, padding: "8px 10px", verticalAlign: "top", width: 130, color: "var(--ne-text-3)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </td>
      {BUREAUS.map((b) => {
        const vals = field[b] || [];
        return (
          <td key={b} style={{ padding: "8px 10px", verticalAlign: "top", borderLeft: "1px solid var(--ne-silver-2)", fontSize: 13 }}>
            {vals.length === 0 ? <span style={{ color: "var(--ne-muted)" }}>—</span> : vals.map((v, i) => <div key={i}>{v}</div>)}
          </td>
        );
      })}
    </tr>
  );
}

function fmtMoney(n: number | undefined): string {
  if (n === undefined || n === null) return "—";
  return "$" + n.toLocaleString();
}

export function ParsedReportModal({
  open,
  reportId,
  onClose,
}: {
  open: boolean;
  reportId: string | null;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedReport | null>(null);
  const [tab, setTab] = useState<TabKey>("personal");

  useEffect(() => {
    if (!open || !reportId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setParsed(null);
      try {
        const r = await reportsApi.getParsed(reportId);
        if (cancelled) return;
        setParsed(r.parsed as ParsedReport);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || "Could not load the report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, reportId]);

  // Pick the first non-empty tab once data lands.
  useEffect(() => {
    if (!parsed) return;
    const order: TabKey[] = ["personal", "tradelines", "inquiries", "public", "other"];
    const present: TabKey[] = order.filter((k) => {
      if (k === "personal") return hasPersonal(parsed);
      if (k === "tradelines") return hasTradelines(parsed);
      if (k === "inquiries") return hasInquiries(parsed);
      if (k === "public") return hasPublic(parsed);
      return hasOther(parsed);
    });
    if (present.length && !present.includes(tab)) setTab(present[0]);
    // eslint-disable-next-line
  }, [parsed]);

  const tabs: { key: TabKey; show: boolean }[] = parsed ? [
    { key: "personal",   show: hasPersonal(parsed) },
    { key: "tradelines", show: hasTradelines(parsed) },
    { key: "inquiries",  show: hasInquiries(parsed) },
    { key: "public",     show: hasPublic(parsed) },
    { key: "other",      show: hasOther(parsed) },
  ] : [];

  return (
    <Modal
      open={open}
      title="Credit report"
      onClose={onClose}
      wide
    >
      <div>
        {loading && <p className="muted small">Loading report…</p>}
        {error && <p className="error-text">{error}</p>}
        {parsed && (
          <>
            {parsed.scores && (
              <div style={{ display: "flex", gap: 18, marginBottom: 12, flexWrap: "wrap" }}>
                {BUREAUS.map((b) => parsed.scores?.[b] !== undefined && (
                  <div key={b} style={{ border: "1px solid var(--ne-silver-1)", padding: "8px 12px", minWidth: 110 }}>
                    <div style={{ fontSize: 11, color: "var(--ne-muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{b}</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{parsed.scores?.[b]}</div>
                  </div>
                ))}
              </div>
            )}

            <div role="tablist" style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--ne-silver-1)", marginBottom: 14 }}>
              {tabs.filter(t => t.show).map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    background: "transparent",
                    border: 0,
                    padding: "10px 14px",
                    fontSize: 13,
                    fontWeight: tab === t.key ? 700 : 500,
                    color: tab === t.key ? "var(--ne-text)" : "var(--ne-muted)",
                    borderBottom: tab === t.key ? "2px solid var(--ne-text)" : "2px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  {TAB_LABELS[t.key]}
                </button>
              ))}
            </div>

            {tab === "personal" && parsed.personal_info && (
              <div role="tabpanel">
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--ne-silver-1)" }}>
                  <thead>
                    <tr style={{ background: "var(--ne-silver-3)" }}>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ne-text-3)" }}>Field</th>
                      {BUREAUS.map((b) => (
                        <th key={b} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ne-text-3)", borderLeft: "1px solid var(--ne-silver-4)" }}>{b}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fieldRow("Names", parsed.personal_info.names)}
                    {fieldRow("Addresses", parsed.personal_info.addresses)}
                    {fieldRow("Employers", parsed.personal_info.employers)}
                    {fieldRow("Phones", parsed.personal_info.phones)}
                    {fieldRow("Date of birth", parsed.personal_info.dob)}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "tradelines" && parsed.tradelines && (
              <div role="tabpanel" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--ne-silver-1)", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--ne-silver-3)" }}>
                      <th style={th}>Creditor</th>
                      <th style={th}>Acct #</th>
                      <th style={th}>Balance</th>
                      <th style={th}>Status</th>
                      <th style={th}>Opened</th>
                      <th style={th}>Bureaus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.tradelines.map((t, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--ne-silver-4)", background: t.is_negative ? "var(--ne-warning-bg)" : undefined }}>
                        <td style={td}>
                          <strong>{t.creditor_name}</strong>
                          {t.category && <div style={{ fontSize: 11, color: "var(--ne-muted)" }}>{t.category}</div>}
                        </td>
                        <td style={td}>{t.account_number_last4 ? `****${t.account_number_last4}` : "—"}</td>
                        <td style={td}>
                          {BUREAUS.map((b) => t.balances?.[b] !== undefined && (
                            <div key={b}><span style={{ fontSize: 10, color: "var(--ne-muted)", textTransform: "uppercase" }}>{b[0]}</span> {fmtMoney(t.balances?.[b])}</div>
                          ))}
                          {(!t.balances || Object.keys(t.balances).length === 0) && <span style={{ color: "var(--ne-muted)" }}>—</span>}
                        </td>
                        <td style={td}>
                          {BUREAUS.map((b) => t.statuses?.[b] && (
                            <div key={b}><span style={{ fontSize: 10, color: "var(--ne-muted)", textTransform: "uppercase" }}>{b[0]}</span> {t.statuses?.[b]}</div>
                          ))}
                          {(!t.statuses || Object.keys(t.statuses).length === 0) && <span style={{ color: "var(--ne-muted)" }}>—</span>}
                        </td>
                        <td style={td}>{t.date_opened || "—"}</td>
                        <td style={td}>{(t.bureaus || []).join(", ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "inquiries" && parsed.inquiries && (() => {
              const inquiries = parsed.inquiries || [];
              const linkedCount = inquiries.filter((q) => q.is_disputable === false).length;
              const disputableCount = inquiries.length - linkedCount;
              const linkedBadge: CSSProperties = {
                display: "inline-block",
                padding: "1px 6px",
                fontSize: 11,
                lineHeight: 1.4,
                background: "var(--ne-silver-3)",
                color: "var(--ne-text-3)",
                border: "1px solid var(--ne-silver-1)",
                whiteSpace: "nowrap",
              };
              const disputableBadge: CSSProperties = {
                display: "inline-block",
                padding: "1px 6px",
                fontSize: 11,
                lineHeight: 1.4,
                background: "var(--ne-success-bg)",
                color: "var(--ne-success-text)",
                border: "1px solid var(--ne-success-border)",
                whiteSpace: "nowrap",
              };
              return (
                <div role="tabpanel">
                  <div style={{ fontSize: 12, color: "var(--ne-text-3)", marginBottom: 8 }}>
                    {inquiries.length} total / {linkedCount} linked / {disputableCount} disputable
                    <span style={{ marginLeft: 6, color: "var(--ne-muted)" }}>
                      (linked = matches an open tradeline from the same creditor; excluded from findings)
                    </span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--ne-silver-1)", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "var(--ne-silver-3)" }}>
                        <th style={th}>Creditor</th>
                        <th style={th}>Bureau</th>
                        <th style={th}>Date</th>
                        <th style={th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((q, i) => {
                        const linked = q.is_disputable === false;
                        return (
                          <tr key={i} style={{ borderTop: "1px solid var(--ne-silver-4)" }}>
                            <td style={td}>{q.creditor_name}</td>
                            <td style={td}>{q.bureau}</td>
                            <td style={td}>{q.inquiry_date || "—"}</td>
                            <td style={td}>
                              {linked ? (
                                <span
                                  style={linkedBadge}
                                  title={
                                    q.linked_account_creditor
                                      ? `Linked to open account: ${q.linked_account_creditor}`
                                      : "Linked to an open account from the same creditor"
                                  }
                                >
                                  Linked to open account — not disputable
                                </span>
                              ) : (
                                <span style={disputableBadge}>Disputable</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {tab === "public" && parsed.public_records && (
              <div role="tabpanel">
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--ne-silver-1)", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--ne-silver-3)" }}>
                      <th style={th}>Kind</th>
                      <th style={th}>Bureau</th>
                      <th style={th}>Filed</th>
                      <th style={th}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.public_records.map((pr, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--ne-silver-4)" }}>
                        <td style={td}>{pr.kind}</td>
                        <td style={td}>{pr.bureau}</td>
                        <td style={td}>{pr.filing_date || "—"}</td>
                        <td style={td}>{fmtMoney(pr.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "other" && (
              <div role="tabpanel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {parsed.tradelines && parsed.tradelines.some(isCollectionOrChargeOff) && (
                  <section>
                    <h3 style={{ margin: "4px 0 8px 0", fontSize: 14 }}>Collections / Charge-offs</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--ne-silver-1)", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "var(--ne-silver-3)" }}>
                          <th style={th}>Creditor</th>
                          <th style={th}>Status</th>
                          <th style={th}>Balance</th>
                          <th style={th}>Bureaus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.tradelines.filter(isCollectionOrChargeOff).map((t, i) => (
                          <tr key={i} style={{ borderTop: "1px solid var(--ne-silver-4)", background: "var(--ne-warning-bg)" }}>
                            <td style={td}><strong>{t.creditor_name}</strong></td>
                            <td style={td}>{Object.values(t.statuses || {}).join(" / ") || (t.category || "")}</td>
                            <td style={td}>
                              {BUREAUS.map((b) => t.balances?.[b] !== undefined && (
                                <div key={b}>{fmtMoney(t.balances?.[b])}</div>
                              ))}
                              {(!t.balances || Object.keys(t.balances).length === 0) && "—"}
                            </td>
                            <td style={td}>{(t.bureaus || []).join(", ") || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                )}
                {parsed.account_summary && Object.keys(parsed.account_summary).length > 0 && (
                  <section>
                    <h3 style={{ margin: "4px 0 8px 0", fontSize: 14 }}>Account summary</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                      {Object.entries(parsed.account_summary).map(([k, v]) => (
                        <div key={k} style={{ border: "1px solid var(--ne-silver-1)", padding: "8px 10px" }}>
                          <div style={{ fontSize: 11, color: "var(--ne-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{k.replace(/_/g, " ")}</div>
                          <div style={{ fontWeight: 700, fontSize: 18 }}>{v ?? "—"}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {parsed.warnings && parsed.warnings.length > 0 && (
                  <section className="banner-warning">
                    <h3 style={{ margin: "4px 0 8px 0", fontSize: 14 }}>Report coverage check</h3>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                      {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const th: CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ne-text-3)" };
const td: CSSProperties = { padding: "8px 10px", verticalAlign: "top" };
